// The Call log section on the customer detail page (Session ABT): the inbound calls logged against
// one customer, newest first, filterable by outcome and date range, with a "Log a call" button and
// per-row edit/delete. Self-contained: it loads its own data and owns the create/edit modal.
//
// Session AQS, W-1: "Raise work" from here too, not only from the standalone (deliberately
// unrouted, kept-on-disk) Call log page -- the office UI restructure audit found this the ONE real
// gap: CallLogPage.tsx already had the full accept-as-work flow, this component simply never wired
// it up. Reuses the SAME two modals CallLogPage.tsx uses (never a second implementation), plus a
// Quote route neither host offered before this session (AcceptCallAsWorkModal's own change).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CALL_OUTCOMES } from "../../lib/api";
import type { CallLogDto, CallOutcome, CallLogListParams } from "../../lib/api";
import { LogCallModal } from "../../components/LogCallModal";
import { Button } from "../../components/Button";
import { CallLogTable } from "../../components/CallLogTable";
import { AcceptCallAsWorkModal } from "./AcceptCallAsWorkModal";
import { RaiseWorkModal, raisedWorkMessage } from "./RaiseWorkModal";

const inputCls =
  "rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";

export function CustomerCallLog({
  customerId,
  customerName,
  // Bumped by the page's header "Log a call" button to open the create modal from outside.
  openSignal,
}: {
  customerId: string;
  customerName: string;
  openSignal?: number;
}) {
  const [rows, setRows] = useState<CallLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [outcome, setOutcome] = useState<CallOutcome | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CallLogDto | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Session AQS, W-1: mirrors CallLogPage.tsx's own accepting/raising pair exactly -- `accepting` is
  // the call whose "is this real work?" question is open, `raising` is the call the four-type raise
  // form is open for, kept separate because the second is reached THROUGH the first.
  const [accepting, setAccepting] = useState<CallLogDto | null>(null);
  const [raising, setRaising] = useState<CallLogDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    const params: CallLogListParams = {};
    if (outcome) params.outcome = outcome;
    if (from) params.from = from;
    if (to) params.to = to;
    return api.customers
      .callLogs(customerId, params)
      .then((r) => setRows(r.items))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [customerId, outcome, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  // Open the create modal when the page header button signals it.
  useEffect(() => {
    if (openSignal && openSignal > 0) {
      setEditing(null);
      setModalOpen(true);
    }
  }, [openSignal]);

  async function remove(id: string) {
    setDeletingId(id);
    try {
      await api.callLogs.remove(id);
      await load();
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg text-ele-blue">Call log</h2>
        <Button size="sm" onClick={() => { setEditing(null); setModalOpen(true); }}>
          Log a call
        </Button>
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body">Outcome</label>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value as CallOutcome | "")} className={inputCls}>
            <option value="">All</option>
            {CALL_OUTCOMES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        </div>
        {(outcome || from || to) && (
          <button type="button" onClick={() => { setOutcome(""); setFrom(""); setTo(""); }} className="font-menu text-xs tracking-wide text-ele-body">Clear</button>
        )}
      </div>

      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}

      {loading ? (
        <p className="py-4 text-sm text-ele-body">Loading calls...</p>
      ) : error ? (
        <p role="alert" className="py-4 text-sm text-ele-strong">Could not load the call log. Please try again in a moment.</p>
      ) : (
        <CallLogTable
          rows={rows}
          onEdit={(row) => { setEditing(row); setModalOpen(true); }}
          onDelete={remove}
          onRaiseWork={(row) => { setNotice(null); setAccepting(row); }}
          deletingId={deletingId}
        />
      )}

      {/* Session AQS, W-1: is this call real work, and which kind? Same question, same two prior
          routes, plus Quote, as the standalone Call log page already asked. */}
      {accepting && (
        <AcceptCallAsWorkModal
          call={accepting}
          onClose={() => setAccepting(null)}
          onCallout={() => {
            const params = new URLSearchParams({ new: "log", callLogId: accepting.id });
            if (accepting.customerId) params.set("customerId", accepting.customerId);
            navigate(`/office/callouts?${params.toString()}`);
          }}
          onPlannedWork={() => {
            setRaising(accepting);
            setAccepting(null);
          }}
          onQuote={() => {
            navigate("/office/quotes/new", {
              state: {
                prefill: { customerName: accepting.customerName ?? "" },
                ...(accepting.customerId ? { link: { customerId: accepting.customerId } } : {}),
              },
            });
            setAccepting(null);
          }}
        />
      )}

      {raising && (
        <RaiseWorkModal
          preset={{
            type: "repair",
            callLogId: raising.id,
            ...(raising.customerId ? { customerId: raising.customerId } : {}),
            ...(raising.customerName ? { customerName: raising.customerName } : {}),
          }}
          onClose={() => setRaising(null)}
          onRaised={(result) => {
            setRaising(null);
            setNotice(raisedWorkMessage(result));
            void load();
          }}
        />
      )}

      <LogCallModal
        open={modalOpen}
        editing={editing}
        lockedCustomer={editing ? null : { id: customerId, name: customerName }}
        onClose={() => setModalOpen(false)}
        onSaved={(created) => {
          void load();
          // Reachable after "+ Log a call" too (Session AQS, W-1): a brand-new call opens straight
          // onto the same "is this real work?" question, without the office hunting for the row it
          // just logged in the list below. An EDIT save carries no `created` row and asks nothing --
          // correcting a past call is not a new piece of work.
          if (created) setAccepting(created);
        }}
      />
    </section>
  );
}
